import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Send, ArrowRight, Upload, SkipForward, Loader2 } from "lucide-react";
import { Linkify } from "@/lib/linkify";

/**
 * Phase K-demo — Full-screen CEO first-view.
 *
 * Shown automatically to users whose `agency.identity_status != "approved"`
 * on their first visit to `/dashboard` each session (redirect in Dashboard.jsx).
 * User can:
 *   - Answer the CEO's structured onboarding questions (company, website,
 *     logo, services, location, target audience, competitors + URLs).
 *   - Skip to dashboard (soft reminder stays on the dashboard CEO card).
 *   - Hit "Complete Agency Setup" to jump to /settings for deeper edit.
 *
 * On each answer we PATCH /api/agency so nothing is lost mid-conversation;
 * the same payload is surfaced to every subsequent CEO call.
 */
const SKIP_SESSION_KEY = "aivos_ceo_onboard_session_dismissed";

// Ordered question list. Each step knows which agency field it writes to
// and whether to accept free text or a file upload. Empty answers are
// treated as "skipped this field" so the user can blitz through.
const STEPS = [
    { id: "agency_name",        label: "What's your agency / company name?",           field: "agency_name",       placeholder: "e.g. RIFA Productions" },
    { id: "website",            label: "What's your website URL?",                     field: "website",           placeholder: "e.g. rifa.studio or https://rifa.studio" },
    { id: "logo",               label: "Upload your logo (optional)",                  field: "logo",              kind: "logo" },
    { id: "services",           label: "What services do you offer? (comma-separated)", field: "services",          placeholder: "Brand Films, Ad Campaigns, AI Video Production" },
    { id: "location",           label: "Where are you based?",                         field: "location",          placeholder: "e.g. Surat, Gujarat, India" },
    { id: "target_clients",     label: "Who are your target audience / clients?",      field: "target_clients",    placeholder: "e.g. D2C brands, real estate, launch campaigns",   multiline: true },
    { id: "competitors",        label: "Name 3 competitors you benchmark against.",    field: "_competitor_names", placeholder: "Studio A, Studio B, Agency C", multiline: true, writesTo: "target_clients_competitors_hint" },
    { id: "competitor_urls",    label: "Drop their websites / Instagram links (one per line, optional)", field: "_competitor_urls", placeholder: "https://…\\n@handle", multiline: true, writesTo: "target_clients_competitors_hint" },
    { id: "instagram",          label: "Your Instagram handle or URL? (optional)",     field: "instagram",         placeholder: "@handle" },
    { id: "linkedin",           label: "Your LinkedIn URL? (optional)",                field: "linkedin",          placeholder: "linkedin.com/company/…" },
];

export default function CeoOnboarding() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [agency, setAgency] = useState(null);
    const [step, setStep] = useState(0);
    const [value, setValue] = useState("");
    const [busy, setBusy] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [transcript, setTranscript] = useState([]);
    const fileRef = useRef(null);
    const tRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/agency");
                setAgency(data);
                const greet = buildGreeting(user, data);
                setTranscript([{ role: "ceo", text: greet }, { role: "ceo", text: STEPS[0].label }]);
            } catch (e) {
                toast.error(`Could not load agency: ${e.userMessage || e.message}`);
            }
        })();
    // eslint-disable-next-line
    }, []);

    useEffect(() => { tRef.current?.scrollTo(0, tRef.current.scrollHeight); }, [transcript]);

    const current = STEPS[step];

    const persistValue = async (cur, raw) => {
        // Map the current step onto a PUT /agency payload.
        const payload = {};
        const val = (raw || "").trim();
        if (!val) return;
        if (cur.id === "services") {
            payload.services = val.split(",").map(s => s.trim()).filter(Boolean);
        } else if (cur.field && !cur.field.startsWith("_")) {
            payload[cur.field] = val;
        }
        // Competitor metadata goes onto `target_clients` as a trailing hint
        // so existing prompts pick it up without a new schema change.
        if (cur.field === "_competitor_names") {
            payload.target_clients = `${agency?.target_clients || ""}\nCompetitors: ${val}`.trim();
        }
        if (cur.field === "_competitor_urls") {
            payload.target_clients = `${agency?.target_clients || ""}\nCompetitor links: ${val}`.trim();
        }
        if (Object.keys(payload).length === 0) return;
        const { data } = await api.put("/agency", payload);
        setAgency(data);
    };

    const advance = async (answerText) => {
        const userSaid = (answerText || "").trim();
        const nextTranscript = [...transcript, { role: "user", text: userSaid || "(skipped)" }];
        setTranscript(nextTranscript);
        setValue("");
        if (userSaid && current) {
            try { await persistValue(current, userSaid); }
            catch (e) { toast.error(`Save failed: ${e.userMessage || e.message}`); }
        }
        if (step + 1 < STEPS.length) {
            const nx = STEPS[step + 1];
            setTranscript([...nextTranscript, { role: "ceo", text: nx.label }]);
            setStep(step + 1);
        } else {
            setTranscript([...nextTranscript, { role: "ceo", text:
                "That's enough to work with. Want me to run an intelligence pass on what you've shared?" }]);
            setStep(STEPS.length); // signals final step
        }
    };

    const uploadLogo = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const t = localStorage.getItem("aivos_token") || "";
            const res = await fetch(`${BACKEND_URL}/api/agency/logo`, {
                method: "POST",
                headers: { Authorization: `Bearer ${t}` },
                body: fd,
            });
            if (!res.ok) throw new Error(`${res.status}`);
            await res.json();
            const { data } = await api.get("/agency");
            setAgency(data);
            toast.success("Logo uploaded");
            advance("(uploaded)");
        } catch (e) {
            toast.error(`Upload failed: ${e.message}`);
        } finally { setBusy(false); }
    };

    const analyze = async () => {
        setAnalyzing(true);
        try {
            const { data } = await api.post("/agency/analyze");
            setAgency(data.agency);
            setTranscript(t => [...t, { role: "ceo", text:
                "Draft identity built. Review and approve it on the Settings page whenever you're ready. I'll remember what we discussed." }]);
            toast.success("Agency identity drafted");
        } catch (e) {
            toast.error(`Analysis failed: ${e.userMessage || e.message}`);
        } finally { setAnalyzing(false); }
    };

    const skipToDashboard = () => {
        sessionStorage.setItem(SKIP_SESSION_KEY, "1");
        nav("/dashboard");
    };

    const submit = () => { if (!busy) advance(value); };

    const missing = detectMissing(agency, transcript);

    return (
        <div className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col" data-testid="ceo-onboarding-page">
            {/* Top bar with skip */}
            <header className="border-b border-zinc-900 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-sm border border-[#FF4400]/40 bg-[#FF4400]/10 flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-[#FF4400]" />
                    </div>
                    <div>
                        <div className="label-micro text-[#FF4400]">FURMAIS · CEO Agent</div>
                        <div className="text-sm text-zinc-300">Personalized onboarding</div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        data-testid="ceo-onboard-complete-setup"
                        onClick={() => nav("/settings")}
                        className="px-3 py-1.5 text-xs border border-zinc-800 bg-zinc-950 hover:bg-zinc-900 rounded-sm"
                    >
                        Complete in Settings
                    </button>
                    <button
                        type="button"
                        data-testid="ceo-onboard-skip"
                        onClick={skipToDashboard}
                        className="px-3 py-1.5 text-xs text-zinc-500 hover:text-zinc-200 inline-flex items-center gap-1"
                    >
                        <SkipForward className="w-3.5 h-3.5" /> Skip for now
                    </button>
                </div>
            </header>

            {/* Transcript */}
            <div ref={tRef} className="flex-1 overflow-y-auto scrollbar-thin px-6 py-6 max-w-3xl w-full mx-auto space-y-3">
                {transcript.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[85%] px-4 py-3 rounded-sm text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-white text-black" : "bg-[#141414] border border-zinc-800 text-zinc-100"}`}>
                            {m.role !== "user" && <div className="label-micro mb-1 text-[#FF4400]">CEO</div>}
                            <Linkify text={m.text} withCopy={m.role !== "user"} />
                        </div>
                    </div>
                ))}

                {/* Smart nudges — shown once the user reaches the final step */}
                {step >= STEPS.length && missing.length > 0 && (
                    <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-3" data-testid="ceo-onboard-missing">
                        <div className="text-[11px] font-mono uppercase tracking-wider text-amber-400 mb-2">Smart nudge</div>
                        <ul className="text-xs text-zinc-200 space-y-1 list-disc pl-5">
                            {missing.map((m, i) => <li key={i}>{m}</li>)}
                        </ul>
                    </div>
                )}
            </div>

            {/* Composer */}
            <div className="border-t border-zinc-900 bg-[#0a0a0a] px-6 py-4">
                <div className="max-w-3xl mx-auto">
                    {step < STEPS.length ? (
                        <div className="flex items-end gap-2 flex-wrap">
                            {current.kind === "logo" ? (
                                <div className="flex items-center gap-2 flex-1">
                                    <input
                                        ref={fileRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp"
                                        className="hidden"
                                        onChange={e => uploadLogo(e.target.files?.[0])}
                                    />
                                    <button
                                        type="button"
                                        data-testid="ceo-onboard-upload-logo"
                                        onClick={() => fileRef.current?.click()}
                                        disabled={busy}
                                        className="px-4 py-2 border border-zinc-800 rounded-sm bg-zinc-950 hover:bg-zinc-900 inline-flex items-center gap-2 text-sm disabled:opacity-50"
                                    >
                                        <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Upload logo"}
                                    </button>
                                    <button
                                        type="button"
                                        data-testid="ceo-onboard-skip-step"
                                        onClick={() => advance("")}
                                        className="px-3 py-2 text-sm text-zinc-500 hover:text-zinc-200"
                                    >
                                        Skip this step →
                                    </button>
                                </div>
                            ) : current.multiline ? (
                                <textarea
                                    data-testid="ceo-onboard-input"
                                    value={value}
                                    onChange={e => setValue(e.target.value)}
                                    onKeyDown={e => { if (e.key === "Enter" && e.ctrlKey) submit(); }}
                                    placeholder={current.placeholder}
                                    rows={3}
                                    className="flex-1 bg-black border border-zinc-800 rounded-sm px-3 py-2 text-sm focus:border-zinc-500 outline-none resize-y"
                                />
                            ) : (
                                <input
                                    data-testid="ceo-onboard-input"
                                    value={value}
                                    onChange={e => setValue(e.target.value)}
                                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) submit(); }}
                                    placeholder={current.placeholder}
                                    className="flex-1 bg-black border border-zinc-800 rounded-sm px-3 py-2 text-sm focus:border-zinc-500 outline-none"
                                />
                            )}
                            {current.kind !== "logo" && (
                                <button
                                    type="button"
                                    data-testid="ceo-onboard-next"
                                    onClick={submit}
                                    disabled={busy}
                                    className="btn-primary px-4 py-2 text-sm disabled:opacity-50"
                                >
                                    <Send className="w-4 h-4" /> Send
                                </button>
                            )}
                            <div className="w-full text-[10px] font-mono uppercase tracking-wider text-zinc-600 mt-2 flex items-center gap-2">
                                <span>Step {step + 1} / {STEPS.length}</span>
                                <span className="text-zinc-800">·</span>
                                <button
                                    type="button"
                                    data-testid="ceo-onboard-skip-step"
                                    onClick={() => advance("")}
                                    className="text-zinc-500 hover:text-zinc-200"
                                >
                                    Skip this step
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                            <button
                                type="button"
                                data-testid="ceo-onboard-analyze"
                                onClick={analyze}
                                disabled={analyzing}
                                className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50"
                            >
                                {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                {analyzing ? "Analyzing…" : "Run Agency Intelligence"}
                            </button>
                            <button
                                type="button"
                                data-testid="ceo-onboard-continue-dashboard"
                                onClick={skipToDashboard}
                                className="px-4 py-2 text-sm border border-zinc-800 bg-zinc-950 hover:bg-zinc-900 rounded-sm inline-flex items-center gap-2"
                            >
                                Continue to dashboard <ArrowRight className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => nav("/settings")}
                                className="px-3 py-2 text-xs text-zinc-500 hover:text-zinc-200"
                            >
                                Edit manually
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ---- helpers ----

function buildGreeting(user, agency) {
    const name = (user?.name || user?.email?.split("@")[0] || "there").trim();
    const agName = (agency?.agency_name || "").trim();
    if (agName) {
        return `Welcome back, ${name} from ${agName}. Let me finish personalizing your workspace — I'll ask a few quick questions so every brief, strategy, and export reflects your agency.`;
    }
    return `Welcome, ${name}. Before we begin, I'd love to understand your agency better so I can work like your personalized creative CEO. I'll ask a few quick questions — you can skip any of them.`;
}

function detectMissing(agency, transcript) {
    const out = [];
    if (!agency) return out;
    if (!agency.logo_path) out.push("Upload a logo whenever you're ready — I'll use it for branded exports and analyse your dominant colours.");
    const services = agency.services || [];
    if (services.length < 2) out.push("Add 2+ services so I can tag the right agents to each brief.");
    const tc = (agency.target_clients || "").toLowerCase();
    if (!tc.includes("competitor")) out.push("Drop 3 competitor names and their websites — we'll use them in the Competitor Analyst stage.");
    if (!agency.website) out.push("Share your website URL so I can pull tone + hero copy.");
    return out.slice(0, 4);
}
